import { literals } from "@/constants/literals";
import { useState } from "react";

import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { useNavigate } from "react-router-dom";
import type { CapturedPhoto, ReceptionStep, GroupData } from "@/types/types.d";
import { CameraCapture } from "../../components/CameraCapture";
import Modal from "@/components/modal/Modal";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { BASE_URL } from "@/constants/constants";
import useReceptionDataStore from "@/store/useReceptionData.store";
import ReceptionOrderHeader from "../components/ReceptionOrderHeader";
import { useDevice } from "@/store/useDevice";
import ReceptionGroupMobileInputs from "../components/ReceptionGroupMobileInputs";
import ReceptionGroupTabletInputs from "../components/ReceptionGroupTabletInputs";

const initial_status = {
  description: "",
  extraInformation: "",
  visibleForClient: true,
  photos: [],
  id: "",
};

const assignGroupId = () => {
  const now = new Date();
  return now.getTime();
};

const ReceptionNewGroupPage = () => {
  const [formData, setFormData] = useState<GroupData>(initial_status);
  const [viewModal, setViewModal] = useState(false);
  const [groupId, setGroupId] = useState(assignGroupId());
  const [description, setDescription] = useState("");
  const [extraInformation, setExtraInformation] = useState("");

  const { addReceptionGroupData, receptionData } = useReceptionDataStore();
  const { isMobile } = useDevice();

  const navigate = useNavigate();

  const handleOnChange = (
    id: string,
    value: string | boolean | CapturedPhoto[],
  ) => {
    setFormData((prevState) => {
      const tmp: any = { ...prevState };
      tmp[id] = value;
      return tmp;
    });
  };

  const isValid = () => {
    if (receptionData.step === literals.inNoServ) {
      if (description === "Others") {
        return formData.photos.length > 0 && extraInformation.trim() !== "";
      }
      return formData.photos.length > 0 && description.trim() !== "";
    }
    return formData.photos.length > 0;
  };

  const changeDescription = (value: string) => {
    setDescription(value);
    if (value === "") {
      handleOnChange("description", "");
      return;
    }
    const entireDescription =
      extraInformation.trim() !== "" ? `${value}: ${extraInformation}` : value;
    handleOnChange("description", entireDescription);
  };

  const changeExtraInformation = (value: string) => {
    setExtraInformation(value);
    if (value.trim() === "") {
      handleOnChange("description", description);
    } else {
      handleOnChange("description", `${description}: ${value}`);
    }
  };

  const handleOnSubmit = (
    e: React.BaseSyntheticEvent<
      Event,
      EventTarget & HTMLFormElement,
      EventTarget
    >,
  ) => {
    e.preventDefault();
    if (!isValid()) return;
    setViewModal(true);
  };

  const goToNextCapturePhase = () => {
    if (!isValid()) return;
    addReceptionGroupData({ ...formData, id: groupId });
    navigate(`${BASE_URL}/capture/reception/resume`);
  };

  const cleanPhotosData = () => {
    if (!isValid()) return;
    addReceptionGroupData({ ...formData, id: groupId });
    setGroupId(assignGroupId());
    setFormData(initial_status);
    setDescription("");
    setExtraInformation("");
    setViewModal(false);
  };

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <ReceptionOrderHeader />
      <form
        onSubmit={handleOnSubmit}
        className="flex flex-col flex-1 justify-between"
      >
        <div className="w-full px-4 md:px-14 pt-4 md:flex-1 md:flex md:flex-col md:justify-between">
          <div className="w-full flex-1">
            <CameraCapture
              onChange={(photos) => handleOnChange("photos", photos)}
              maxPhotos={10}
              photos={formData?.photos || undefined}
            />
          </div>
          {isMobile ? (
            <ReceptionGroupMobileInputs
              step={receptionData.step as ReceptionStep}
              formData={formData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          ) : (
            <ReceptionGroupTabletInputs
              step={receptionData.step as ReceptionStep}
              formData={formData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          )}
        </div>
        <div className="py-4 flex flex-col pt-4 border-t border-t-ib-pt-color-gray-300 md:flex md:flex-row md:justify-center">
          <div className="px-4 md:capture-button">
            <PrimaryButton
              size="medium"
              label={literals.continue}
              colour="red"
              disabled={!isValid()}
              type="submit"
              onClick={() => {}}
            />
          </div>
        </div>
      </form>
      <Modal
        title={literals.continue}
        opened={viewModal}
        onClose={() => setViewModal(false)}
      >
        <div className="w-full flex flex-col gap-y-4">
          <SecondaryButton
            colour="red"
            label={literals.uploadMorePhotos}
            onClick={cleanPhotosData}
          />
          <PrimaryButton
            colour="red"
            icon="checkUnderline"
            label={literals.seeAllPhotos}
            onClick={goToNextCapturePhase}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ReceptionNewGroupPage;
